/**
 * This module manages the Laravel Echo / Pusher add-in
 * @file
 * @author Brendan Miller <brendan@thebrandingfarm.com>
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */

// Pusher is needed for Echo
// eslint-disable-next-line
import Pusher from 'pusher-js'
import Echo from 'laravel-echo'
import api from '@/api'

import { 
		VUEX_ECHO_INIT, VUEX_ECHO_INITIALIZED,
		VUEX_ECHO_HANDLE_MODEL_BROADCAST_EVENT, VUEX_ECHO_LOGOUT } from '@/store/constants/echo'

import { 
	VUEX_NOTIFICATIONS_NEW_NOTIFICATION } from '@/store/constants/notifications'
import { 
	VUEX_AUTH_LOGOUT } from '@/store/constants/auth'

import { 
	VUEX_JOB_GET_COMMENT } from '@/store/constants/jobs'

import { 
	VUEX_FILE_HANDLE_FILE_NOTIFICATION } from '@/store/constants/files'

import {
	VUEX_CALENDAR_HANDLE_FILE_NOTIFICATION } from '@/store/constants/calendarItems'

import {
	VUEX_USERS_HANDLE_FILE_NOTIFICATION
} from '@/store/constants/users'


// Uncomment to debug if you are having issues with the echo connection
// if (process.env.APP_ENV !== 'production') {
//	console.info('ENV',process.env);
// 	Pusher.logToConsole = true;
// }


const echoConfig = {
	broadcaster: 'pusher',
	key: process.env.VUE_APP_PUSHER_APP_KEY,
	wsHost: process.env.VUE_APP_SOCKET_BASE_URL,
	authEndpoint: `${process.env.VUE_APP_API_BASE_URL}/broadcasting/auth`,
	wsPort: 6001,
	wssPort: 443, // has to be proxied by nginx in our setup
	disableStats: true,
	enabledTransports: ['ws', 'wss']
	//encrypted: process.env.VUE_APP_API_BASE_URL && process.env.VUE_APP_API_BASE_URL.includes('https://')
}

// let echoInstance = null


const state = {
	echoInstance: null
}

const getters = {}

const actions = {
	[VUEX_ECHO_INIT]: ({rootState, dispatch}) => {

		let echoInstance = new Echo(echoConfig)

		echoInstance.connector.pusher.config.auth.headers['Authorization'] = `Bearer ${rootState.auth.token}`

		echoInstance.connector.pusher.connection
			.bind('connected', function(){
				console.info('Echo pusher connected', echoInstance.socketId());
				console.info('Notifications initialized! Private user channel:', rootState.auth.currentUser.id);

				// commit(VUEX_ECHO_INITIALIZED, echoInstance) //  VUEX has a fit when you modify it directly
			})
			.bind('disconnected', function() {
				console.info('Echo pusher disconnected')
			})
			.bind('connecting', function(attemptNumber) {
				console.info('Echo pusher reconnecting', attemptNumber)
			})
			.bind('unavailable', function(attemptNumber) {
				console.info('Echo pusher unavailable', attemptNumber)
			})
			.bind('failed', function(attemptNumber) {
				console.info('Echo pusher failed', attemptNumber)
			})
			.bind('error', function( err ) {
				console.error('Echo error', err)
			});


		// private channel for user specific messages
		echoInstance.private(`App.User.${rootState.auth.currentUser.id}`)
			.listen('NotificationBroadcastEvent', async (e) => {
				console.info('BroadcastNotificationCreated received', e)

				if (e) {
					await dispatch(VUEX_NOTIFICATIONS_NEW_NOTIFICATION, e)
				}
			})

		// general channel for all model change messages
		echoInstance.private(`sila2-model-broadcast`)
			.listen('ModelBroadcastEvent', async (e) => {
				console.info('ModelBroadcastEvent received', e)

				if (e) dispatch(VUEX_ECHO_HANDLE_MODEL_BROADCAST_EVENT, e)
			})
	},

	[VUEX_ECHO_HANDLE_MODEL_BROADCAST_EVENT]: async ({rootState, dispatch, commit}, payload) => {
		// let model = payload.model_class.split('\\')[1]


		const SILA_ECHO_MODEL_JOB_COMMENT = 'App\\JobComment',
			  SILA_ECHO_MODEL_FILE = 'App\\File'


		switch(payload.model_class) {
			// JobComment
			case SILA_ECHO_MODEL_JOB_COMMENT:
				if (!rootState.jobs.job) return

				let loaded_job_id = rootState.jobs.job.id
				if (payload.parent_id === loaded_job_id){
					setTimeout(() => {
                        if (typeof rootState.jobs.jobComments.findIndex === "function") {
                            var index = rootState.jobs.jobComments.findIndex(item => item.id == payload.model_id)
                            if (index == -1) {
                                dispatch(VUEX_JOB_GET_COMMENT, payload.model_id) // load job comment
                            }
                        }
					}, 500)
				}

				break

			// File
			case SILA_ECHO_MODEL_FILE:
				// TODO: GENERAL (DO NOT REMOVE)
				// Come back and, once we know all of these, consolidate / refactor
				await api.get(`/files/${payload.model_id}`).then(async (response) => {
					// Jobs one
					dispatch(VUEX_FILE_HANDLE_FILE_NOTIFICATION, payload)

					// TODO: ONLY UPDATE WHEN ROUTE MATCHES TOAN ?
					dispatch(VUEX_CALENDAR_HANDLE_FILE_NOTIFICATION, payload)


					// User Avatars
					if (response.data.data) {
						let fileData = response.data.data
						if (fileData.usage_type === SILA_FILE_USAGE_TYPE_USER_AVATAR ||
							fileData.usage_type === SILA_FILE_USAGE_TYPE_USER_AVATAR_LARGE ||
							fileData.usage_type === SILA_FILE_USAGE_TYPE_USER_AVATAR_SMALL
						) {
							dispatch(VUEX_USERS_HANDLE_FILE_NOTIFICATION, fileData)
						}
					}
				}).catch((err) => {
					console.error("File no longer exists")
				})
				break


			// Everything else
			default:
				break
		}
	},

	[VUEX_AUTH_LOGOUT]: async ({state, commit}) => {
		if (state.echoInstance) await state.echoInstance.disconnect()
		commit(VUEX_ECHO_LOGOUT)
	},
}

const mutations = {
	[VUEX_ECHO_INITIALIZED]: (state, echoInstance) => {
		state.echoInstance = echoInstance
	},

	[VUEX_ECHO_LOGOUT]: (state) => {
		state.echoInstance = null
	}
}

/**
 * The Laravel Echo manager
 * @module store/modules/echo
 */
export default {
	state,
	getters,
	actions,
	mutations,
}